import { KeyboardArrowDown } from "@mui/icons-material";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select, { MenuProps, OptionProps } from "react-select";

import { MuiButton } from "@ryde/controls";
import { IDropdownItem, PLURAL_KEY, useIsMobile } from "@ryde/shared";

import { useDashboardViewDropdown } from "./dashboard-use-view-dropdown";
import { ViewDropdownMenu } from "./dashboard-view-dropdown-menu";
import {
  ViewDropdownControl,
  ViewDropdownWrapper,
} from "./dashboard-view-dropdown.elements";
import { ViewDropdownOption } from "./dashboard-view-dropdown.option";
import { viewDropdownSelectStyles } from "./dashboard-view-dropdown.select-styles";

export const DashboardViewDropdown: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();

  const {
    onViewDropdownChange,
    handleUpdateView,
    handleDeleteView,
    isOpen,
    handleToggle,
    handleClose,
    mappedViews,
    editViewHandler,
    setDefaultHandler,
    activeViewId,
    defaultViewId,
  } = useDashboardViewDropdown();

  const activeViewDropdownValue: IDropdownItem | undefined = useMemo(() => {
    if (!activeViewId) {
      return undefined;
    }

    return mappedViews.find(x => String(x.value) === String(activeViewId));
  }, [activeViewId, mappedViews]);

  const selectStylesComputed = useMemo(() => {
    return viewDropdownSelectStyles(isMobile);
  }, [isMobile]);

  return (
    <ViewDropdownWrapper
      isOpen={isOpen}
      onClose={handleClose}
      target={
        <MuiButton onClick={handleToggle} e2eData="views-button">
          {t(`view.${PLURAL_KEY}`)}
          <KeyboardArrowDown data-e2e="svg-arrow-down" />
        </MuiButton>
      }
    >
      <Select
        value={activeViewDropdownValue}
        // eslint-disable-next-line jsx-a11y-x/no-autofocus -- intentional: focus the field when opened by user action
        autoFocus
        backspaceRemovesValue={false}
        data-e2e="views-select"
        components={{
          DropdownIndicator: null,
          IndicatorSeparator: null,
          Control: ViewDropdownControl,
          Menu: menuProps => <ViewDropdownMenu {...(menuProps as MenuProps)} />,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          Option: (props: OptionProps) => {
            return (
              <ViewDropdownOption
                {...props}
                handleDelete={handleDeleteView}
                handleUpdate={handleUpdateView}
                activeViewId={activeViewId}
                defaultViewId={defaultViewId}
                editViewHandler={editViewHandler}
                setDefaultHandler={setDefaultHandler}
              />
            );
          },
        }}
        controlShouldRenderValue={false}
        isClearable
        menuIsOpen
        options={mappedViews}
        onChange={onViewDropdownChange}
        placeholder={t(`view.formLabel.search`)}
        noOptionsMessage={() => t(`errors.noOptionsMessage`)}
        styles={selectStylesComputed}
        tabSelectsValue={false}
      />
    </ViewDropdownWrapper>
  );
};
